import type { VNode } from "preact";
import { useState, useCallback, useEffect, useRef, useMemo } from "preact/hooks";
import type { HostEntry } from "../types";
import { getRedirectX } from "../settings";
import {fmtPing} from "@/lib/strings";

type Props = {
  hosts: HostEntry[];
  /** The user's preferred instance, if set and present in the host list. */
  preferredHost: HostEntry | null;
};

/**
 * Parse whatever the user typed into a Nitter-relative path.
 *
 * Accepts:
 *   - "elonmusk"                       -> /elonmusk
 *   - "@elonmusk"                      -> /elonmusk
 *   - "elonmusk/status/123456"         -> /elonmusk/status/123456
 *   - "https://twitter.com/elonmusk"   -> /elonmusk
 *   - "https://x.com/elonmusk/status/123"
 *   - "/search?q=foo"                  -> /search?q=foo
 *
 * Anything that doesn't look like a Twitter URL is treated as a raw
 * Nitter path (stripped of a leading slash, then re-prefixed).
 */
function parseInput(raw: string): string {
  let s = raw.trim();
  if (!s) return "";

  // Twitter / X URL — take the path + query.
  try {
    const u = new URL(s);
    if (u.hostname === "twitter.com" || u.hostname === "x.com" || u.hostname === "www.twitter.com" || u.hostname === "www.x.com") {
      const p = u.pathname + u.search;
      return p === "/" ? "/" : p.replace(/\/+$/, "");
    }
  } catch {
    // not a URL — fine, fall through
  }

  // Strip leading @ and leading slashes.
  s = s.replace(/^[@/]+/, "");
  // Ensure it starts with a slash.
  return "/" + s;
}

/** Detect if the input looks like a domain (e.g. "nitter.example.com"). */
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

function looksLikeDomain(raw: string): boolean {
  const s = raw.trim().toLowerCase();
  if (!s) return false;
  // Must not contain spaces or slashes (those are paths).
  if (/[\s/]/.test(s)) return false;
  // Must have at least one dot.
  if (!s.includes(".")) return false;
  return DOMAIN_RE.test(s);
}

/** Is the input a twitter.com / x.com URL? */
function isXUrl(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    return ["twitter.com", "x.com", "www.twitter.com", "www.x.com"].includes(u.hostname);
  } catch {
    return false;
  }
}
type Suggestion = {
  kind: "host";
  host: HostEntry;
};

export function BrowseBar({ hosts, preferredHost }: Props): VNode {
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  /** True only when the user actively highlighted a suggestion
   *  (arrow keys or hover). Enter then picks that host; otherwise Enter
   *  navigates to the typed path (honouring redirect/preferred settings). */
  const [highlighted, setHighlighted] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build suggestion list: preferred first, then top healthy hosts by score.
  const suggestions = useMemo<Suggestion[]>(() => {
    const healthy = hosts
      .filter((h) => h.healthy && h.domain !== preferredHost?.domain)
      .sort((a, b) => b.effective - a.effective);
    const list = preferredHost && preferredHost.healthy
      ? [preferredHost, ...healthy]
      : healthy;
    return list.slice(0, 8).map((host) => ({ kind: "host" as const, host }));
  }, [hosts, preferredHost]);

  // Reset activeIdx whenever suggestions change.
  useEffect(() => {
    setActiveIdx(0);
    setHighlighted(false);
  }, [suggestions]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  /** Navigate to /v/?domain=<domain> with the optional path (the static resolver). */
  const navigateToHost = useCallback(
    (domain: string, path?: string) => {
      const p = path || parseInput(value) || "/";
      // Build the /v/ URL with ?domain= so the resolver uses this
      // specific host and serves the banner chrome instead of a raw
      // /proxy/ redirect.
      const viewUrl = new URL("/v" + (p !== "/" ? p : ""), window.location.origin);
      viewUrl.searchParams.set("domain", domain);
      window.location.href = viewUrl.pathname + viewUrl.search;
    },
    [value],
  );

  const submit = useCallback(
    (e: Event) => {
      e.preventDefault();
      const trimmed = value.trim();
      if (!trimmed) return;

      // If the user actively highlighted a suggestion, pick that host.
      if (open && highlighted && suggestions[activeIdx]) {
        navigateToHost(suggestions[activeIdx].host.domain);
        return;
      }

      // If the input looks like a domain (e.g. "nitter.example.com"),
      // treat it as an explicit host pick.
      if (looksLikeDomain(trimmed)) {
        navigateToHost(trimmed);
        return;
      }

      // From here on we're navigating a Twitter/X path — either the raw
      // path typed by the user or the path of an x.com URL.
      const path = parseInput(trimmed);
      if (!path || path === "/") return;

      // User setting: skip Nitter entirely and go to x.com.
      if (getRedirectX()) {
        const target = isXUrl(trimmed)
          ? trimmed.replace(/^https?:\/\/(www\.)?twitter\.com/i, "https://x.com")
          : "https://x.com" + path;
        window.location.href = target;
        return;
      }

      // Preferred instance (if healthy) beats the weighted-random pick.
      if (preferredHost && preferredHost.healthy) {
        navigateToHost(preferredHost.domain);
        return;
      }

      // Otherwise, treat it as a path/handle/URL → /v/<path> (auto-pick).
      window.location.href = "/v" + path;
    },
    [value, open, highlighted, suggestions, activeIdx, navigateToHost, preferredHost],
  );

  const pickHost = useCallback(
    (host: HostEntry) => {
      navigateToHost(host.domain);
    },
    [navigateToHost],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setHighlighted(true);
        setActiveIdx((i) => (i + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setOpen(true);
        setHighlighted(true);
        setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === "Escape") {
        setOpen(false);
      } else if (e.key === "Enter" && open && highlighted && suggestions[activeIdx]) {
        // Let the form submit pick the active suggestion.
      }
    },
    [suggestions, open, activeIdx],
  );

  const showPreview = value.trim().length > 0;
  const previewPath = parseInput(value) || "";
  const isDomain = looksLikeDomain(value.trim());
  const redirectOn = getRedirectX();

  const previewText = !showPreview
    ? `→ pick a host above, or type a handle/path and press Enter`
    : isDomain
      ? `→ /v/?domain=${value.trim()}  (explicit host)`
      : redirectOn
        ? `→ https://x.com${previewPath === "/" ? "/home" : previewPath}  (x.com redirect is ON — change it in Settings)`
        : preferredHost && preferredHost.healthy && previewPath !== "/" && previewPath
          ? `→ /v/?domain=${preferredHost.domain}${previewPath}  (your preferred instance)`
          : previewPath && previewPath !== "/"
            ? `→ /v${previewPath}  (auto-pick)  ·  or pick a host above`
            : `→ pick a host above, or type a handle/path and press Enter`;

  return (
    <section class="browse">
      <div class="browse__inner">
        <div class="browse__eyebrow">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          Browse Twitter via Nitter
        </div>
        <h2 class="browse__title">
          Type a handle or URL, or pick a healthy instance below.
        </h2>

        <form class="browse__form" onSubmit={submit} autocomplete="off">
          <div class="browse__combo" ref={wrapRef}>
            <div class="browse__input-wrap">
              <span class="browse__prefix">/v</span>
              <input
                ref={inputRef}
                class="browse__input"
                type="text"
                placeholder="elonmusk  ·  @NASA  ·  https://x.com/…  ·  or a nitter domain…"
                value={value}
                onInput={(e) => {
                  setValue((e.target as HTMLInputElement).value);
                  // Keep the dropdown open while typing — don't hide it.
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                autocapitalize="none"
                autocorrect="off"
                spellcheck={false}
                aria-label="Twitter handle, URL, or Nitter domain"
                aria-expanded={open}
                aria-autocomplete="list"
                role="combobox"
              />
              <button class="browse__submit" type="submit" disabled={!value.trim()}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
                Open
              </button>
            </div>

            {open && suggestions.length > 0 && (
              <ul class="browse__dropdown" role="listbox">
                <li class="browse__dropdown-header">
                  Pick an instance
                  {showPreview && previewPath !== "/" && !isDomain && (
                    <span class="browse__dropdown-hint">
                      path: {previewPath}
                    </span>
                  )}
                  {isDomain && (
                    <span class="browse__dropdown-hint">
                      ↦ {value.trim()} (explicit domain)
                    </span>
                  )}
                </li>
                {suggestions.map((s, i) => (
                  <li
                    key={s.host.domain}
                    role="option"
                    aria-selected={i === activeIdx}
                    class={`browse__option${i === activeIdx ? " browse__option--active" : ""}`}
                    onMouseEnter={() => {
                      setActiveIdx(i);
                      setHighlighted(true);
                    }}
                    onMouseDown={(e) => {
                      // mousedown so it fires before blur closes the dropdown
                      e.preventDefault();
                      pickHost(s.host);
                    }}
                  >
                    <span
                      class="browse__option-dot"
                      style={`background: ${s.host.effective >= 75 ? "var(--healthy)" : s.host.effective >= 50 ? "var(--warning)" : "var(--danger)"};`}
                    />
                    <span class="browse__option-domain">{s.host.domain}</span>
                    {preferredHost?.domain === s.host.domain && (
                      <span class="browse__option-pref" title="Your preferred instance — saved in this browser">
                        ★ preferred
                      </span>
                    )}
                    <span class="browse__option-meta">
                      <span class="browse__option-score">{s.host.effective.toFixed(0)}</span>
                      <span class="browse__option-ping">{fmtPing(s.host.ping_avg, "")}</span>
                    </span>
                  </li>
                ))}
                <li class="browse__dropdown-footer">
                  <kbd>↑</kbd><kbd>↓</kbd> to navigate
                  <span class="browse__dropdown-sep">·</span>
                  <kbd>Enter</kbd> to pick
                  <span class="browse__dropdown-sep">·</span>
                  <kbd>Esc</kbd> to close
                </li>
              </ul>
            )}
          </div>
          <div class={`browse__preview${showPreview ? " browse__preview--visible" : ""}`}>
            {previewText}
          </div>
        </form>
      </div>
    </section>
  );
}
